// Sample field mappings for Zoho CRM <-> Shopify integration
// (Zoho Books / Inventory support was removed — CRM is the only target service.)
//
// The service ships:
//   - 3 mandatory default settings (create triggers) — enabled: true
//   - 6 optional default settings (update/delete triggers) — enabled: false
//
// Optional settings are created alongside mandatory ones so the webhooks are
// already registered with Shopify, but they stay disabled until the user
// flips them on in the Field Mapping UI.

// ━━━ Zoho CRM Mappings ━━━
const crmContactRows = (shop) => [
  { Zoho: "Last_Name", Shopify: "last_name", default: false },
  { Zoho: "First_Name", Shopify: "first_name", default: false },
  { Zoho: "Email", Shopify: "email", default: false },
  { Zoho: "Phone", Shopify: "phone", default: false },
  { Zoho: "Mailing_Street", Shopify: "default_address.address1", default: false },
  { Zoho: "Mailing_City", Shopify: "default_address.city", default: false },
  { Zoho: "Mailing_State", Shopify: "default_address.province", default: false },
  { Zoho: "Mailing_Country", Shopify: "default_address.country", default: false },
  { Zoho: "Mailing_Zip", Shopify: "default_address.zip", default: false },
  
  { Zoho: "easyshopifyforzohocrm__Shopify_ID_EXT", Shopify: "customer_id", default: false, locked: true },
  { Zoho: "easyshopifyforzohocrm__Shopify_Shop_URL", Shopify: shop || "", default: true, locked: true },
];

// Sales Order rows depend on the currently-selected shop (Shopify_Shop_URL
// default value), so this is a factory rather than a static array.
//
// Shopify orders sync into the Zoho CRM **Sales Orders** module (previously
// Deals). The order header maps to `Subject`, and the order's `line_items[]`
// map into the Sales Order `Ordered_Items` subform.
//
// The line items map to the standard "Ordered Items" subform fields (present
// with these api_names in every standard Sales Orders module):
//   Product_Name — LOOKUP to the Products module, resolved by matching the
//     line item's `product_id` to the Zoho Product's `Shopify_ID_EXT`
//     (written by products/create). Products must sync before orders. It is
//     the ONLY row targeting Product_Name — a lookup takes a single source.
//   Quantity / List_Price / Description — straight value copies.
// If an org uses a renamed / custom subform, the client re-maps these in the UI.
const crmSalesOrderRows = (shop) => [
  { Zoho: "Subject", Shopify: "name", default: false },
  {
    // Subform parent: Shopify line_items[] → Zoho Ordered_Items subform.
    Zoho: "Ordered_Items",
    Shopify: "line_items",
    default: false,
    subRows: [
      // Product lookup (key: product_id → Products.Shopify_ID_EXT).
      { Zoho: "Product_Name", Shopify: "line_items[].name", default: false },
      { Zoho: "Quantity", Shopify: "line_items[].quantity", default: false },
      { Zoho: "List_Price", Shopify: "line_items[].price", default: false },
      { Zoho: "Description", Shopify: "line_items[].title", default: false },
    ],
  },
  { Zoho: "easyshopifyforzohocrm__Shopify_ID_EXT", Shopify: "id", default: false, locked: true },
  { Zoho: "easyshopifyforzohocrm__Shopify_Shop_URL", Shopify: shop || "", default: true, locked: true },
];

// CRM → Shopify (CRM2Shopify) Sales Order rows.
//
// Same field correspondences as crmSalesOrderRows, but two things differ for
// the OUTBOUND direction, so this is a separate factory:
//   1. Product lookup source. Inbound (Shopify2CRM) resolves the Ordered_Items
//      `Product_Name` lookup from `line_items[].product_id`. Outbound writes
//      the product name to Shopify, so it reads `Product_Name` and maps it to
//      `line_items[].name` instead.
//   2. Rows carry the full stamped type metadata (shopifyType / zohoType /
//      lookupModuleApiName / …) the outbound workflow function expects, since
//      no webhook payload is available to re-derive types from.
const crmSalesOrderRowsC2S = (shop) => [
  {
    Shopify: "name",
    Zoho: "Subject",
    default: false,
    shopifyType: "string",
    zohoType: "text",
    lookupModuleApiName: "",
    subformModuleApiName: "",
    subRows: [],
    hint: "",
    locked: false,
  },
  {
    Shopify: "line_items",
    Zoho: "Ordered_Items",
    default: false,
    shopifyType: "array",
    zohoType: "subform",
    lookupModuleApiName: "",
    subformModuleApiName: "",
    subRows: [
      {
        Shopify: "line_items[].name",
        Zoho: "Product_Name",
        default: false,
        shopifyType: "string",
        zohoType: "lookup",
        lookupModuleApiName: "Products",
        subformModuleApiName: "",
        subRows: [],
        hint: "",
        locked: false,
      },
      {
        Shopify: "line_items[].quantity",
        Zoho: "Quantity",
        default: false,
        shopifyType: "integer",
        zohoType: "double",
        lookupModuleApiName: "",
        subformModuleApiName: "",
        subRows: [],
        hint: "",
        locked: false,
      },
      {
        Shopify: "line_items[].price",
        Zoho: "List_Price",
        default: false,
        shopifyType: "string",
        zohoType: "currency",
        lookupModuleApiName: "",
        subformModuleApiName: "",
        subRows: [],
        hint: "",
        locked: false,
      },
      {
        Shopify: "line_items[].title",
        Zoho: "Description",
        default: false,
        shopifyType: "string",
        zohoType: "textarea",
        lookupModuleApiName: "",
        subformModuleApiName: "",
        subRows: [],
        hint: "",
        locked: false,
      },
    ],
    hint: "",
    locked: false,
  },
  {
    Shopify: "id",
    Zoho: "easyshopifyforzohocrm__Shopify_ID_EXT",
    default: false,
    shopifyType: "integer",
    zohoType: "text",
    lookupModuleApiName: "",
    subformModuleApiName: "",
    subRows: [],
    hint: "",
    locked: true,
  },
  {
    Shopify: shop || "",
    Zoho: "easyshopifyforzohocrm__Shopify_Shop_URL",
    default: true,
    shopifyType: "",
    zohoType: "text",
    lookupModuleApiName: "",
    subformModuleApiName: "",
    subRows: [],
    hint: "",
    locked: true,
  },
];

const crmProductRows = (shop) => [
  { Zoho: "Product_Name", Shopify: "title", default: false },
  { Zoho: "Description", Shopify: "body_html", default: false },
  { Zoho: "Product_Category", Shopify: "product_type", default: false },
  { Zoho: "easyshopifyforzohocrm__Shopify_ID_EXT", Shopify: "id", default: false, locked: true },
  { Zoho: "easyshopifyforzohocrm__Shopify_Shop_URL", Shopify: shop || "", default: true, locked: true },
];

// Delete webhooks only receive { id: ... } in the payload, so delete
// mappings carry a single row tying Shopify's id to the extension-owned
// Shopify_ID_EXT field the sync function uses to find the Zoho record.
const crmDeleteRow = [
  { Zoho: "easyshopifyforzohocrm__Shopify_ID_EXT", Shopify: "id", default: false },
];

const buildCRMSetting = ({
  orgId,
  shop,
  moduleName,
  rows,
  trigger,
  triggerApiName,
  upsertField,
  recordAction,
  enabled,
  // Sync direction of the rule. Webhook-driven defaults are "Shopify2CRM";
  // the CRM-workflow-driven defaults pass "CRM2Shopify" (and never get a
  // Shopify webhook — ensureDefaultSetup skips registration for them).
  direction = "Shopify2CRM",
  extras = {},
}) => ({
  org_id: orgId,
  conn_name: "easyshopifyforzohocrm",
  module_name: moduleName,
  setting_type: "systemDefined",
  setting_schema: {
    rows,
    shop,
    module: moduleName,
    enabled,
    service: "Zoho CRM",
    trigger,
    upsertField,
    recordAction,
    triggerApiName,
    direction,
    ...extras,
  },
});

// RelatedList settings power the widget embedded on Zoho records. They're NOT
// shop-scoped — one per module is sufficient regardless of how many Shopify
// stores are connected. The widget uses these fields to find the Shopify
// record and shop URL for the currently-open Zoho record.
const buildCRMRelatedListSetting = ({ orgId, moduleName, extras = {} }) => ({
  org_id: orgId,
  conn_name: "easyshopifyforzohocrm",
  module_name: moduleName,
  setting_type: "relatedList",
  setting_schema: {
    IdField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    shopUrlField: "easyshopifyforzohocrm__Shopify_Shop_URL",
    ...extras,
  },
});

export const sampleCRMRelatedListMappings = (orgId) => [
  buildCRMRelatedListSetting({ orgId, moduleName: "Contacts" }),
  buildCRMRelatedListSetting({ orgId, moduleName: "Sales_Orders" }),
  buildCRMRelatedListSetting({ orgId, moduleName: "Products" }),
];

export const sampleCRMMappings = (orgId, shop) => [
  // ─── MANDATORY (enabled) ───────────────────────────────
  buildCRMSetting({
    orgId, shop,
    moduleName: "Contacts",
    rows: crmContactRows(shop),
    trigger: "CUSTOMERS_CREATE",
    triggerApiName: "customers/create",
    upsertField: "Email",
    recordAction: "upsert",
    enabled: true,
    extras: { createAccount: true },
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Sales_Orders",
    rows: crmSalesOrderRows(shop),
    trigger: "ORDERS_CREATE",
    triggerApiName: "orders/create",
    upsertField: "Subject",
    recordAction: "upsert",
    enabled: true,
    extras: { createContact: true },
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Products",
    rows: crmProductRows(shop),
    trigger: "PRODUCTS_CREATE",
    triggerApiName: "products/create",
    upsertField: "Product_Name",
    recordAction: "upsert",
    enabled: true,
    // (was `createDeal: true` — dropped now that the default order flow targets
    // Sales Orders, not Deals, so product syncs no longer spawn orphan Deals.)
  }),

  // ─── OPTIONAL (disabled) ───────────────────────────────
  buildCRMSetting({
    orgId, shop,
    moduleName: "Contacts",
    rows: crmContactRows(shop),
    trigger: "CUSTOMERS_UPDATE",
    triggerApiName: "customers/update",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "upsert",
    enabled: false,
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Contacts",
    rows: crmDeleteRow,
    trigger: "CUSTOMERS_DELETE",
    triggerApiName: "customers/delete",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "delete",
    enabled: false,
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Products",
    rows: crmProductRows(shop),
    trigger: "PRODUCTS_UPDATE",
    triggerApiName: "products/update",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "upsert",
    enabled: false,
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Products",
    rows: crmDeleteRow,
    trigger: "PRODUCTS_DELETE",
    triggerApiName: "products/delete",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "delete",
    enabled: false,
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Sales_Orders",
    rows: crmSalesOrderRows(shop),
    trigger: "ORDERS_UPDATED",
    triggerApiName: "orders/updated",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "upsert",
    enabled: false,
  }),
  buildCRMSetting({
    orgId, shop,
    moduleName: "Sales_Orders",
    rows: crmDeleteRow,
    trigger: "ORDERS_DELETE",
    triggerApiName: "orders/delete",
    upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
    recordAction: "delete",
    enabled: false,
  }),

  // ─── CRM → SHOPIFY ──────────────────────────────────────────────────────
  // Fired by the Zoho → Shopify outbound workflows (see setZohoWorkflowsStatus),
  // NOT by Shopify webhooks — no webhook is registered for these. The row
  // pairs are the same field correspondences as the webhook rules; the
  // backend reads them right-to-left for this direction (Zoho value → Shopify
  // field). `upsertField` names the CRM field whose value identifies the
  // target Shopify record (Shopify_ID_EXT holds the Shopify id written by
  // the inbound sync).
  //
  // ONLY the crm/record/create rule is seeded per module (enabled, following
  // the Dashboard's "Both Way Sync" master toggle). Update and delete rules
  // are deliberately NOT auto-created — pushing CRM edits/deletions to a live
  // store is an explicit per-rule opt-in the client sets up themselves via
  // Add Mapping / custom workflows.
  ...["Contacts", "Sales_Orders", "Products"].map((moduleName) => {
    const rowsFor = {
      Contacts: crmContactRows(shop),
      // CRM → Shopify uses the outbound-specific Sales Order rows (Product_Name
      // maps to line_items[].name, full type metadata). The inbound
      // Shopify2CRM rules above keep crmSalesOrderRows (product_id lookup).
      Sales_Orders: crmSalesOrderRowsC2S(shop),
      Products: crmProductRows(shop),
    }[moduleName];
    return buildCRMSetting({
      orgId, shop, moduleName,
      rows: rowsFor,
      trigger: "CRM_RECORD_CREATE",
      triggerApiName: "crm/record/create",
      upsertField: "easyshopifyforzohocrm__Shopify_ID_EXT",
      recordAction: "upsert",
      enabled: true,
      direction: "CRM2Shopify",
    });
  }),
];
